/**
 * AMPD DCMS2 — Form field runtime registry
 *
 * Runtime (post-render) API over dataset edit-form fields: baseline tracking
 * (startValue), dirty state, semantic change emission with origins, reset to
 * the last persisted value and an in-place AJAX refresh of a single field.
 * Complements the pre-render builder API (formBuilder.js setValue/setValidation)
 * — the builder configures markup, this module manages the LIVE form.
 *
 * Design constraints (non-negotiable):
 *  - Lazy: zero cost until the first register()/field() call — no DOM scan,
 *    no listeners, no objects on pages that do not opt in.
 *  - Per-document: every window (host, dataset iframe, modal iframe) owns an
 *    independent registry; field ids only need to be unique per document.
 *    Cross-frame access is explicit (window.parent.DCMSFields) and must be
 *    resolve-on-use — never cache a registry reference across navigations.
 *  - One delegated capture listener pair (input/change) per document,
 *    regardless of the number of registered fields.
 *  - Programmatic value application NEVER emits native change/input events;
 *    consumers listen to the semantic events below instead.
 *
 * Public API (window.DCMSFields):
 *
 *   DCMSFields.register(fieldId, adapter) → FieldHandle
 *     Registers a field with a custom adapter. Any code may call this —
 *     framework components, project components, scripts outside the framework.
 *     Adapter contract (duck-typed, only read() is required):
 *       el         — root element of the field (event anchor; default: the input)
 *       read()     — canonical current value
 *       apply(v, ctx) — write the value AND re-hydrate the component UI;
 *                    ctx = { origin, payload? } (payload only on refresh)
 *       normalize(v)  — canonical string for comparisons (default String(v))
 *     Re-registering the same fieldId replaces the adapter but keeps the
 *     baseline (component re-init after a DOM swap must not reset dirty).
 *
 *   DCMSFields.field(fieldId) → FieldHandle | null
 *     Returns the registered handle, or lazily wraps a plain input
 *     ([name=fieldId] / #fieldId inside the edit form) with the default
 *     value-based adapter.
 *
 *   FieldHandle:
 *     getValue()            — current canonical value
 *     setValue(v, origin?)  — programmatic write (default origin 'setValue')
 *     isDirty()             — value differs from the baseline
 *     startValue()          — the baseline value
 *     reset()               — re-apply the baseline (origin 'reset')
 *     refresh(opts?)        — fetch fresh server state (?datasetFieldRefresh=…)
 *                             and apply it (origin 'refresh'); rejects with
 *                             {skipped:'dirty'} when the field is dirty and
 *                             opts.force is not set; last-wins on overlap
 *     rebaseline()          — adopt the CURRENT value as the new baseline
 *
 *   FieldHandle.save(opts?)     — instant save of THIS field: single-column
 *                                 PATCH (merge semantics) to the REST record
 *                                 endpoint, with the optimistic-locking
 *                                 baseline riding along. Requires the dataset
 *                                 opt-in (InterfaceOptions::setInstantSaveEnabled)
 *                                 unless opts.force. One in-flight save per
 *                                 field — an overlapping call coalesces into a
 *                                 trailing re-save of the newest value.
 *
 *   DCMSFields.isDirty()        — aggregate over registered fields
 *   DCMSFields.rebaselineAll()  — after a save (called automatically on
 *                                 dcms:dock:saved)
 *   DCMSFields.refreshables()   — field ids the server declared refreshable
 *                                 (capability map __DCMS_FIELD_REFRESH__)
 *   DCMSFields.instantSavables()— field ids eligible for instant save ([] when
 *                                 the dataset did not opt in)
 *   DCMSFields.version          — API level for feature detection
 *
 * Instant save (dataset opt-in): fields listed in the capability map save
 * themselves automatically on COMMIT (a trusted `change` event) — each as a
 * PATCH of that one column. Extra semantic events:
 *   dcms:field:saved          detail { fieldId, field, response }
 *   dcms:field:save-rejected  detail { fieldId, field, reason, message,
 *                                      fieldErrors?, conflictBase? }
 * A 409 (`reason: 'conflict'`) means someone else saved meanwhile — the field
 * stays dirty and the fresh baseline is NOT adopted silently; showing the
 * situation to the user is the consumer's job.
 *
 * Semantic events (bubbling, dispatched on the adapter's element):
 *   dcms:field:changed        detail { fieldId, field, origin }
 *     Fired on EVERY change. May fire at keyboard frequency for rich fields —
 *     consumers MUST debounce heavy work. No value in the detail on purpose:
 *     read it lazily via detail.field.getValue().
 *   dcms:field:dirty-changed  detail { fieldId, field, dirty }
 *     Fired only when the dirty flag flips — cheap to consume for UI state.
 *
 * Origins: 'user' (trusted DOM event), 'setValue' (programmatic write),
 * 'refresh' (server state), 'reset' (back to baseline), 'restore'
 * (autosave draft). Consumers decide what counts as an edit.
 */
(function () {
    'use strict';

    if (window.DCMSFields) { return; }   // double-include guard (bundle + src)

    var handles = Object.create(null);   // fieldId → FieldHandle
    var delegated = false;
    var saveHooked = false;

    /* ---- helpers --------------------------------------------------------- */

    function cssEscape(s) {
        return (window.CSS && CSS.escape) ? CSS.escape(s) : String(s).replace(/"/g, '\\"');
    }

    // Capability map emitted by editHandler::renderForm() (EDIT mode only).
    function capabilityMap() {
        var cap = window.__DCMS_FIELD_REFRESH__;
        return (cap && typeof cap === 'object') ? cap : null;
    }

    /* ---- FieldHandle ------------------------------------------------------ */

    function FieldHandle(fieldId, adapter) {
        this.id = fieldId;
        this.adapter = adapter;
        this._startRaw = this._read();
        this._start = this._norm(this._startRaw);
        this._dirty = false;
        this._reqSeq = 0;
        this._saving = false;     // one in-flight save per field…
        this._savePending = false; // …overlaps coalesce into a trailing re-save
    }

    FieldHandle.prototype = {
        _read: function () {
            try { return this.adapter.read(); } catch (e) { return null; }
        },
        _norm: function (v) {
            try {
                if (this.adapter.normalize) { return this.adapter.normalize(v); }
            } catch (e) { /* fall through to the default */ }
            return String(v == null ? '' : v);
        },
        _apply: function (v, ctx) {
            if (typeof this.adapter.apply === 'function') {
                this.adapter.apply(v, ctx);
            } else if (this.adapter.el && 'value' in this.adapter.el) {
                this.adapter.el.value = (v == null ? '' : v);
            }
        },

        getValue: function () { return this._read(); },
        startValue: function () { return this._startRaw; },
        isDirty: function () { return this._dirty; },

        setValue: function (v, origin) {
            this._apply(v, { origin: origin || 'setValue' });
            this.notifyChange(origin || 'setValue');
        },

        reset: function () {
            this._apply(this._startRaw, { origin: 'reset' });
            this.notifyChange('reset');
        },

        /**
         * Adopt the CURRENT value as the new baseline (after a save, or after
         * fresh server state was applied). Clears dirty without any 'user'
         * origin event.
         */
        rebaseline: function () {
            this._startRaw = this._read();
            this._start = this._norm(this._startRaw);
            if (this._dirty) {
                this._dirty = false;
                emit(this, 'dcms:field:dirty-changed', { dirty: false });
            }
        },

        /**
         * Recompute dirty and emit the semantic events. Adapters call this from
         * their internal change paths; the delegated listener calls it for
         * plain inputs. Cheap: normalizes only THIS field's value.
         */
        notifyChange: function (origin) {
            var dirty = this._norm(this._read()) !== this._start;
            emit(this, 'dcms:field:changed', { origin: origin || 'setValue' });
            if (dirty !== this._dirty) {
                this._dirty = dirty;
                emit(this, 'dcms:field:dirty-changed', { dirty: dirty });
            }
        },

        /**
         * In-place server refresh (?datasetFieldRefresh=<id> on the position
         * URL — full authorization pipeline, JSON envelope). Applies the fresh
         * value with origin 'refresh' and re-baselines. Refuses on a dirty
         * field unless opts.force — never silently overwrite user work.
         * Overlapping calls: last-wins (stale responses are dropped).
         *
         * @returns {Promise<object|null>} the payload, or null (not refreshable
         *          / stale response); rejects on {skipped:'dirty'} and network errors
         */
        refresh: function (opts) {
            var self = this;
            if (this._dirty && !(opts && opts.force)) {
                return Promise.reject({ skipped: 'dirty', fieldId: this.id });
            }
            var cap = capabilityMap();
            var param = (cap && cap.param) || 'datasetFieldRefresh';
            if (cap && Array.isArray(cap.fields) && cap.fields.indexOf(this.id) < 0
                && !(opts && opts.force)) {
                return Promise.resolve(null);   // server declared it not refreshable
            }
            var seq = ++this._reqSeq;
            var url = new URL(window.location.href);
            url.searchParams.set(param, this.id);
            return fetch(url.toString(), {
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            }).then(function (r) {
                if (!r.ok) { throw new Error('field refresh HTTP ' + r.status); }
                return r.json();
            }).then(function (env) {
                if (seq !== self._reqSeq) { return null; }          // stale — dropped
                var payload = env && env.payload;
                if (!payload) { return null; }                       // not refreshable
                if ('value' in payload) {
                    self._apply(payload.value, { origin: 'refresh', payload: payload });
                }
                self.rebaseline();
                self.notifyChange('refresh');
                return payload;
            });
        },

        /**
         * Instant save of THIS field — a single-column PATCH (merge semantics)
         * to the REST record endpoint declared in the capability map. Carries
         * the optimistic-locking baseline (hidden datasetConflictBase) and
         * adopts the fresh one from the response. On success the field is
         * re-baselined (saved = the new reference point) and dcms:field:saved
         * fires; every failure fires dcms:field:save-rejected and leaves the
         * dirty state untouched. One in-flight save per field: an overlapping
         * call marks a pending re-save of whatever the newest value is by then.
         *
         * @returns {Promise<object|null>} response payload, null when skipped
         *          (no opt-in / field not eligible / already queued)
         */
        save: function (opts) {
            var self = this;
            var cap = capabilityMap();
            var eligible = cap && cap.instant && cap.saveUrl
                && Array.isArray(cap.saveFields) && cap.saveFields.indexOf(this.id) >= 0;
            if (!eligible && !(opts && opts.force)) { return Promise.resolve(null); }
            if (!cap || !cap.saveUrl) { return Promise.resolve(null); }
            if (this._saving) { this._savePending = true; return Promise.resolve(null); }
            this._saving = true;

            var body = {};
            body[this.id] = this._read();
            var conflictParam = cap.conflictParam || 'datasetConflictBase';
            var baseEl = document.querySelector('input[name="' + cssEscape(conflictParam) + '"]');
            if (baseEl && baseEl.value) { body[conflictParam] = parseInt(baseEl.value, 10) || 0; }

            var rejected = function (reason, extra) {
                var detail = { reason: reason };
                if (extra) {
                    detail.message = extra.message;
                    if (extra.fieldErrors) { detail.fieldErrors = extra.fieldErrors; }
                    if (extra.conflictBase) { detail.conflictBase = extra.conflictBase; }
                }
                emit(self, 'dcms:field:save-rejected', detail);
                return null;
            };

            var headers = {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            };
            return fetch(cap.saveUrl, {
                method: 'PATCH',
                credentials: 'same-origin',
                headers: window.dcmsCsrf ? window.dcmsCsrf.headers(headers) : headers,
                body: JSON.stringify(body)
            }).then(function (r) {
                return r.json().catch(function () { return null; }).then(function (json) {
                    if (r.ok) {
                        // Fresh optimistic-locking baseline — keep the hidden input
                        // in step, or the NEXT full save reports a false conflict.
                        if (json && json.conflictBase && baseEl) {
                            baseEl.value = String(json.conflictBase);
                        }
                        self.rebaseline();
                        emit(self, 'dcms:field:saved', { response: json });
                        return json;
                    }
                    // Error envelope: { error: {code, message}, ...extra } — the
                    // extras (conflictBase, fieldErrors) ride at the TOP level.
                    var err = (json && json.error) || {};
                    if (r.status === 409) {
                        return rejected('conflict', {
                            message: err.message,
                            conflictBase: json && json.conflictBase
                        });
                    }
                    if (r.status === 422) {
                        return rejected('validation', {
                            message: err.message,
                            fieldErrors: json && json.fieldErrors
                        });
                    }
                    return rejected('error', { message: err.message || ('HTTP ' + r.status) });
                });
            }).catch(function (e) {
                return rejected('network', { message: e && e.message });
            }).then(function (result) {
                self._saving = false;
                if (self._savePending) {
                    self._savePending = false;
                    // Trailing re-save picks up the NEWEST value (read at send time).
                    self.save(opts);
                }
                return result;
            });
        }
    };

    /* ---- events ----------------------------------------------------------- */

    function emit(handle, type, detail) {
        detail.fieldId = handle.id;
        detail.field = handle;   // lazy access: detail.field.getValue()
        var el = handle.adapter.el || document;
        try {
            el.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: detail }));
        } catch (e) { /* a consumer threw — never break the form */ }
    }

    // One capture listener pair per document — N fields still cost 1 pair.
    // Trusted events map to origin 'user'; synthetic ones to 'setValue'.
    //
    // Instant save rides the SAME pair: a trusted `change` (commit — text
    // inputs fire it on blur, selects/checkboxes on pick) of an eligible,
    // dirty field triggers save(). `input` never saves — that would fire at
    // keyboard frequency. Auto-adoption: an eligible plain input that was
    // never register()ed nor field()ed is wrapped on its first commit, so the
    // dataset opt-in works with zero page code.
    function ensureDelegation() {
        if (delegated) { return; }
        delegated = true;
        ['input', 'change'].forEach(function (type) {
            document.addEventListener(type, function (e) {
                var owner = ownerHandle(e.target);
                if (!owner && type === 'change' && instantEligibleTarget(e.target)) {
                    owner = window.DCMSFields.field(e.target.name || e.target.id);
                }
                if (!owner) { return; }
                owner.notifyChange(e.isTrusted ? 'user' : 'setValue');
                if (type === 'change' && e.isTrusted && owner.isDirty()) {
                    var cap = capabilityMap();
                    if (cap && cap.instant && Array.isArray(cap.saveFields)
                        && cap.saveFields.indexOf(owner.id) >= 0) {
                        owner.save();
                    }
                }
            }, true);
        });
    }

    // Is the DOM element an instant-savable form control per the capability map?
    function instantEligibleTarget(el) {
        var cap = capabilityMap();
        if (!cap || !cap.instant || !Array.isArray(cap.saveFields)) { return false; }
        if (!el || !('value' in el)) { return false; }
        var name = el.name || el.id;
        return !!name && cap.saveFields.indexOf(name) >= 0;
    }

    function ownerHandle(target) {
        if (!target || !target.closest) { return null; }
        var name = target.name || target.id;
        if (name && handles[name]) { return handles[name]; }
        // Containment: adapters whose el wraps the actual inputs.
        for (var id in handles) {
            var el = handles[id].adapter.el;
            if (el && el !== target && el.contains && el.contains(target)) {
                return handles[id];
            }
        }
        return null;
    }

    // After a background save (action dock) the server state is fresh — every
    // registered field adopts its current value as the new baseline. Hooked
    // lazily together with delegation, so pages without fields pay nothing.
    function ensureSaveHook() {
        if (saveHooked) { return; }
        saveHooked = true;
        document.addEventListener('dcms:dock:saved', function () {
            window.DCMSFields.rebaselineAll();
        });
    }

    /* ---- default adapter --------------------------------------------------- */

    function defaultAdapter(el) {
        return {
            el: el,
            read: function () { return el.value; },
            apply: function (v) { el.value = (v == null ? '' : v); }
        };
    }

    /* ---- public API -------------------------------------------------------- */

    window.DCMSFields = {
        version: 2,

        register: function (fieldId, adapter) {
            if (!fieldId || !adapter || typeof adapter.read !== 'function') {
                throw new Error('DCMSFields.register: fieldId and adapter.read() are required');
            }
            ensureDelegation();
            ensureSaveHook();
            var existing = handles[fieldId];
            if (existing) {
                // Component re-init after a DOM swap: swap the adapter, keep the
                // baseline — a re-render must not silently clear dirty state.
                existing.adapter = adapter;
                return existing;
            }
            var handle = new FieldHandle(fieldId, adapter);
            handles[fieldId] = handle;
            return handle;
        },

        field: function (fieldId) {
            if (handles[fieldId]) { return handles[fieldId]; }
            var esc = cssEscape(String(fieldId));
            var el = document.querySelector(
                'form[name="datasetEditForm"] [name="' + esc + '"], '
                + 'form[name="datasetEditForm"] #' + esc + ', '
                + '[name="' + esc + '"]'
            );
            if (!el || !('value' in el)) { return null; }
            return this.register(fieldId, defaultAdapter(el));
        },

        fields: function () {
            var out = [];
            for (var id in handles) { out.push(handles[id]); }
            return out;
        },

        isDirty: function () {
            for (var id in handles) {
                if (handles[id].isDirty()) { return true; }
            }
            return false;
        },

        rebaselineAll: function () {
            for (var id in handles) { handles[id].rebaseline(); }
        },

        refreshables: function () {
            var cap = capabilityMap();
            return (cap && Array.isArray(cap.fields)) ? cap.fields.slice() : [];
        },

        instantSavables: function () {
            var cap = capabilityMap();
            return (cap && cap.instant && Array.isArray(cap.saveFields)) ? cap.saveFields.slice() : [];
        }
    };

    // Instant-save datasets need the delegated listeners armed WITHOUT any page
    // code calling the API first (the whole point of the opt-in is zero page
    // code). The capability map script sits in the document head, so it is
    // already evaluated when this module runs. Pages without the flag keep the
    // fully lazy behaviour — nothing is installed here.
    var bootCap = capabilityMap();
    if (bootCap && bootCap.instant) {
        ensureDelegation();
        ensureSaveHook();
    }

    // Load-order signal: this file ships inside the main MODULE bundle, which
    // executes in the deferred-module queue — dynamically appended classic
    // scripts (async=false, the in-order queue) may run EARLIER. A consumer
    // must therefore feature-detect AND fall back to this event:
    //
    //   if (window.DCMSFields) { register(); }
    //   else { document.addEventListener('dcms:fields:ready', register, { once: true }); }
    try {
        document.dispatchEvent(new CustomEvent('dcms:fields:ready'));
    } catch (e) { /* a listener threw — not our failure */ }
})();
